import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Loader2, MapPin, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useFleetLive } from '@/hooks/useFleetLive';
import { useLocationTrackingSettings, useSaveLocationTracking, useSetTechTracking } from '@/hooks/useLocationTracking';
import { formatDocDate } from '@/utils/locale';
import {
  DEFAULT_TRACKING_HOURS, ISO_WEEKDAYS, WEEKDAY_SHORT, formatAge, hoursSummary, minutesSince, parseBusinessHours,
  type FleetTech, type IsoWeekday, type TrackingHours,
} from '@/utils/fleetStatus';

const PERMISSION_LABEL: Record<NonNullable<FleetTech['permission']>, string> = {
  always: 'Allowed',
  while_using: 'Only while the app is open',
  denied: 'Location off',
  services_off: 'Location services off',
  unknown: 'Unknown',
};

function sameHours(a: TrackingHours, b: TrackingHours): boolean {
  return ISO_WEEKDAYS.every((d) => JSON.stringify(a[d] ?? null) === JSON.stringify(b[d] ?? null));
}

function hoursProblem(hours: TrackingHours): string | null {
  for (const d of ISO_WEEKDAYS) {
    const w = hours[d];
    if (w && !(w[0] < w[1])) return `${WEEKDAY_SHORT[d]}: the start time must be before the end time.`;
  }
  return ISO_WEEKDAYS.some((d) => hours[d]) ? null : 'Pick at least one day.';
}

export default function TechTracking() {
  const { timezone } = useCompanyLocale();
  const settings = useLocationTrackingSettings();
  const save = useSaveLocationTracking();
  const fleet = useFleetLive({ realtime: false });
  const setTechTracking = useSetTechTracking();

  const [enabled, setEnabled] = useState(false);
  const [hours, setHours] = useState<TrackingHours>(DEFAULT_TRACKING_HOURS);
  const [noticeTicked, setNoticeTicked] = useState(false);

  const saved = settings.data;
  useEffect(() => {
    if (!saved) return;
    setEnabled(saved.location_tracking_enabled);
    setHours(saved.location_tracking_hours ?? DEFAULT_TRACKING_HOURS);
    setNoticeTicked(false);
  }, [saved]);

  const fromBusinessHours = useMemo(() => parseBusinessHours(saved?.business_hours), [saved?.business_hours]);
  const noticeOnFile = !!saved?.location_notice_confirmed_at;
  const problem = hoursProblem(hours);
  const needsNotice = enabled && !noticeOnFile && !noticeTicked;
  const dirty = !!saved && (enabled !== saved.location_tracking_enabled || !sameHours(hours, saved.location_tracking_hours ?? {}) || noticeTicked);

  const setDay = (d: IsoWeekday, value: [string, string] | null) => setHours((h) => ({ ...h, [d]: value }));

  if (settings.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (settings.error) {
    return (
      <div className="py-8 text-center">
        <AlertCircle className="mx-auto mb-3 h-8 w-8 text-destructive" />
        <p className="mb-4 text-muted-foreground">{(settings.error as Error).message}</p>
        <Button variant="outline" onClick={() => settings.refetch()}>Try again</Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Technician tracking
          </CardTitle>
          <CardDescription>
            The Steddy app shares each technician’s location with the office during the hours below, so you can see
            who is closest on the <Link to="/map" className="text-primary underline-offset-4 hover:underline">Map</Link>.
            Positions are approximate (about 100 m) and update every 15 minutes or so. Nothing is recorded outside
            these hours. Admins can see the route a technician drove for {saved?.location_history_days ?? 30} days,
            then it is deleted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="tracking-enabled" className="text-base">Share technician locations during work hours</Label>
              <p className="text-sm text-muted-foreground">
                Each technician also has to accept a notice in the app before anything is shared.
              </p>
            </div>
            <Switch id="tracking-enabled" checked={enabled} onCheckedChange={setEnabled} />
          </div>

          {noticeOnFile ? (
            <p className="text-sm text-muted-foreground">
              Written notice to technicians confirmed {formatDocDate(saved?.location_notice_confirmed_at, 'd MMM yyyy', timezone)}.
            </p>
          ) : (
            <div className="space-y-2 rounded-md border bg-muted/40 p-3">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="tracking-notice"
                  checked={noticeTicked}
                  onCheckedChange={(v) => setNoticeTicked(v === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="tracking-notice" className="font-normal leading-snug">
                  I’ve given our technicians written notice that their location is tracked during work hours.
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Some places require notice before tracking starts, for example 14 days in New South Wales. Check the
                rules where you operate. This confirmation is recorded with your name and the date.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-base">Tracking hours</Label>
              {fromBusinessHours && !sameHours(fromBusinessHours, hours) && (
                <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => setHours(fromBusinessHours)}>
                  Use business hours ({hoursSummary(fromBusinessHours)})
                </Button>
              )}
            </div>
            <div className="divide-y rounded-md border">
              {ISO_WEEKDAYS.map((d) => {
                const w = hours[d] ?? null;
                return (
                  <div key={d} className="flex flex-wrap items-center gap-3 px-3 py-2">
                    <Switch
                      checked={!!w}
                      onCheckedChange={(on) => setDay(d, on ? ['07:00', '18:00'] : null)}
                      aria-label={`Track on ${WEEKDAY_SHORT[d]}`}
                    />
                    <span className="w-10 text-sm font-medium">{WEEKDAY_SHORT[d]}</span>
                    {w ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          step={900}
                          value={w[0]}
                          onChange={(e) => setDay(d, [e.target.value, w[1]])}
                          className="h-8 w-[120px]"
                          aria-label={`${WEEKDAY_SHORT[d]} start`}
                        />
                        <span className="text-muted-foreground">to</span>
                        <Input
                          type="time"
                          step={900}
                          value={w[1]}
                          onChange={(e) => setDay(d, [w[0], e.target.value])}
                          className="h-8 w-[120px]"
                          aria-label={`${WEEKDAY_SHORT[d]} end`}
                        />
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">Not tracked</span>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">In the company’s time zone ({timezone}).</p>
          </div>

          {problem && <p className="text-sm text-destructive">{problem}</p>}
          {needsNotice && <p className="text-sm text-warning">Confirm written notice above before turning tracking on.</p>}

          <div className="flex justify-end">
            <Button
              disabled={!dirty || !!problem || needsNotice || save.isPending}
              onClick={() => save.mutate({ enabled, hours, noticeConfirmed: noticeTicked })}
            >
              {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Technicians</CardTitle>
          <CardDescription>
            Turn tracking off for someone on leave or working from the office. The phone column shows what each
            technician’s phone last reported.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {fleet.isLoading ? (
            <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : fleet.error ? (
            <p className="text-sm text-muted-foreground">{(fleet.error as Error).message}</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Technician</TableHead>
                    <TableHead>Tracked</TableHead>
                    <TableHead>Notice</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Last report</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(fleet.data?.techs ?? []).map((t) => (
                    <TableRow key={t.tech_id}>
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell>
                        <Switch
                          checked={t.tracking_enabled}
                          disabled={setTechTracking.isPending}
                          onCheckedChange={(on) => setTechTracking.mutate({ techId: t.tech_id, enabled: on, name: t.name })}
                          aria-label={`Track ${t.name}`}
                        />
                      </TableCell>
                      <TableCell>
                        {t.notice_accepted
                          ? <Badge variant="success" className="font-normal">Accepted {t.notice_accepted_at ? formatDocDate(t.notice_accepted_at, 'd MMM', timezone) : ''}</Badge>
                          : <Badge variant="secondary" className="font-normal">Not yet</Badge>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {t.permission ? (
                          <span className={t.permission === 'denied' || t.permission === 'services_off' ? 'text-warning' : ''}>
                            {PERMISSION_LABEL[t.permission]}
                          </span>
                        ) : <span className="text-muted-foreground">No app reports yet</span>}
                        {t.platform && <span className="text-muted-foreground"> · {t.platform === 'ios' ? 'iPhone' : t.platform === 'android' ? 'Android' : t.platform}</span>}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {t.reported_at ? formatAge(minutesSince(t.reported_at)) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
